import { create } from "zustand";
import type { BookingState } from "./useBookingState";
import { initialState as initialBookingState } from "./useBookingState";
import { createJSONStorage, persist } from "zustand/middleware";
import { storage as storageInstance } from "@/lib/storage";
import type { ServiceName } from "@/types/service";
import { differenceInDays } from "date-fns";
import { PackageRecurrenceEnum } from "@/__generated__/graphql";
import { useAuthState } from "@/store/auth";

export type UnfinishedBooking = {
  serviceName: ServiceName;
  bookingState: BookingState;
  timeStamp: Date;
  clientId: string;
};

type UnfinishedBookingState = {
  unfinishedBooking: UnfinishedBooking | null;
  setUnfinishedBooking: (
    unfinishedBooking: Omit<UnfinishedBooking, "timeStamp" | "clientId">,
  ) => void;
  resetUnfinishedBooking: () => void;
};

const storage = createJSONStorage<UnfinishedBookingState>(
  () => storageInstance,
  {
    reviver: (key, value) => {
      if (["timeStamp", "startDate", "endDate"].includes(key)) {
        return new Date(value as string);
      }
      return value;
    },
  },
);

export const useUnfinishedBookingState = create(
  persist<UnfinishedBookingState>(
    (set) => ({
      unfinishedBooking: null,
      setUnfinishedBooking: (unfinishedBooking) => {
        if (
          unfinishedBooking.bookingState.packageDetailsFilter.repeatEvery?.includes(
            PackageRecurrenceEnum.AdHoc,
          )
        ) {
          set({
            unfinishedBooking: {
              ...unfinishedBooking,
              timeStamp: new Date(),
              clientId: useAuthState.getState().data.userInfo.id,
            },
          });
        }
      },

      resetUnfinishedBooking: () => set({ unfinishedBooking: null }),
    }),
    {
      name: "unfinished-booking",
      storage,
      partialize: (state) => {
        const newState = {
          ...state,
        };
        if (state.unfinishedBooking) {
          newState.unfinishedBooking = {
            clientId: state.unfinishedBooking.clientId,
            serviceName: state.unfinishedBooking.serviceName,
            timeStamp: state.unfinishedBooking.timeStamp,
            bookingState: {
              ...initialBookingState,
              packageDetailsFilter:
                state.unfinishedBooking.bookingState.packageDetailsFilter,
              department: state.unfinishedBooking.bookingState.department,
              serviceDetails:
                state.unfinishedBooking.bookingState.serviceDetails,
              currentPath: state.unfinishedBooking.bookingState.currentPath,
              selectedAddress:
                state.unfinishedBooking.bookingState.selectedAddress,
              startDate: state.unfinishedBooking.bookingState.startDate,
              endDate: state.unfinishedBooking.bookingState.endDate,
              selectedWorkers:
                state.unfinishedBooking.bookingState.selectedWorkers,
              packageDetails:
                state.unfinishedBooking.bookingState.packageDetails,
              selectedPackageIds:
                state.unfinishedBooking.bookingState.selectedPackageIds,
              homeBeautyFreeGelServiceIds:
                state.unfinishedBooking.bookingState
                  .homeBeautyFreeGelServiceIds,
              petProfileInfo:
                state.unfinishedBooking.bookingState.petProfileInfo,
            },
          };
        }
        return newState;
      },
      onRehydrateStorage: () => (state) => {
        if (state?.unfinishedBooking) {
          const unfinishedTime = new Date(state.unfinishedBooking.timeStamp);

          // Always check time-based expiration
          if (differenceInDays(new Date(), unfinishedTime) > 2) {
            state.unfinishedBooking = null;
            return;
          }

          // Set up subscription to validate when auth state is ready
          const unsubscribe = useAuthState.subscribe((authState) => {
            if (authState.data.userInfo.id) {
              // Auth state is now ready, validate client ID
              const currentState = useUnfinishedBookingState.getState();
              if (
                currentState.unfinishedBooking &&
                authState.data.userInfo.id !==
                  currentState.unfinishedBooking.clientId
              ) {
                useUnfinishedBookingState.getState().resetUnfinishedBooking();
              }
              // Unsubscribe after validation
              unsubscribe();
            }
          });

          // Also check immediately in case auth is already ready
          const currentClientId = useAuthState.getState().data.userInfo.id;
          if (
            currentClientId &&
            currentClientId !== state.unfinishedBooking.clientId
          ) {
            state.unfinishedBooking = null;
            unsubscribe();
          }
        }
      },
    },
  ),
);
